import { chakra, GridItem, Flex, Text, Box, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import Skeleton from 'ui/shared/chakra/Skeleton';
import * as ContainerWithScrollY from 'ui/shared/ContainerWithScrollY';
import Hint from 'ui/shared/Hint';
import HintPopover from 'ui/shared/HintPopover';

const LabelScrollText = () => (
  <Text fontWeight={ 500 } variant="secondary" fontSize="xs" className="note" align="right">
    Scroll to see more
  </Text>
);

interface LabelProps {
  hint?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  id?: string;
  hasScroll?: boolean;
  type?: 'tooltip' | 'popover';
}

const Label = chakra(({ hint, children, isLoading, id, className, hasScroll, type }: LabelProps) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <GridItem
      id={ id }
      className={ className }
      py={{ base: 2, lg: 2.5 }}
      lineHeight={{ base: 5, lg: 6 }}
    >
      <Flex columnGap={ 2 } alignItems="center">
        { hint && (type === 'popover' ?
          <HintPopover label={ hint } isLoading={ isLoading } my={{ lg: '2px' }}/> :
          <Hint label={ hint } isLoading={ isLoading } my={{ lg: '2px' }}/>) }
        <Skeleton isLoaded={ !isLoading } display="flex" alignItems="center" gap={ 1 }>
          <Text
            as="span"
            fontSize="sm"
            fontWeight={ 600 }
            color={ labelColor }
          >
            { children }
          </Text>
          { hasScroll && <LabelScrollText/> }
        </Skeleton>
      </Flex>
    </GridItem>
  );
});

interface ValueProps {
  children: React.ReactNode;
  className?: string;
}

const Value = chakra(({ children, className }: ValueProps) => {
  return (
    <GridItem
      className={ className }
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      rowGap={ 3 }
      pl={{ base: 0, lg: 0 }}
      py={{ base: 2, lg: 2.5 }}
      lineHeight={{ base: 5, lg: 6 }}
      whiteSpace="nowrap"
      fontWeight={ 500 }
      fontSize="sm"
    >
      { children }
    </GridItem>
  );
});

const ValueWithScroll = chakra(({ children, gradientHeight, onScrollVisibilityChange, className }: ContainerWithScrollY.Props) => {
  return (
    <Value position="relative">
      <ContainerWithScrollY.default
        className={ className }
        gradientHeight={ gradientHeight }
        onScrollVisibilityChange={ onScrollVisibilityChange }
      >
        { children }
      </ContainerWithScrollY.default>
    </Value>
  );
});

export {
  Label,
  Value,
  ValueWithScroll,
};
